<template>
  <div class="image-audit-sub-container">
    <div class="page-top-bar">
      <div class="title-group">
        <h2 class="main-title">图片 AI 内容审核</h2>
        <span class="sub-title">基于多模态大模型的图像理解与合规性检查</span>
      </div>
    </div>

    <el-row :gutter="20">
      <el-col :xs="24" :sm="24" :md="9" :lg="8">
        <div class="sticky-form">
          <el-card shadow="never" class="config-card">
            <div slot="header" class="card-header">
              <span><i class="el-icon-setting"></i> 任务配置</span>
            </div>

            <el-form label-position="top" size="medium">
              <el-form-item label="待审核图片">
                <el-upload
                  class="image-uploader"
                  drag
                  action=""
                  :auto-upload="false"
                  :show-file-list="false"
                  :on-change="handleFileChange"
                >
                  <div v-if="previewUrl" class="upload-preview">
                    <img :src="previewUrl" class="preview-img" />
                    <div class="upload-mask">
                      <i class="el-icon-refresh"></i>
                      <p>更换图片</p>
                    </div>
                  </div>
                  <template v-else>
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">拖拽或<em>点击上传</em></div>
                  </template>
                </el-upload>
              </el-form-item>

              <el-form-item label="检测指令 (Prompts)">
                <div v-for="(item, index) in prompts" :key="index" class="prompt-input-row">
                  <el-input
                    v-model="prompts[index]"
                    placeholder="例如: 图中是否有违禁品？"
                    clearable
                  >
                    <el-button
                      slot="append"
                      icon="el-icon-minus"
                      @click="removePrompt(index)"
                      :disabled="prompts.length <= 1"
                    ></el-button>
                  </el-input>
                </div>
                <el-button
                  type="text"
                  icon="el-icon-circle-plus-outline"
                  class="add-prompt-btn"
                  @click="addPrompt"
                >新增一条检测指令</el-button>
              </el-form-item>

              <el-divider></el-divider>

              <el-button
                type="primary"
                class="submit-btn"
                :loading="submitting"
                @click="submitAudit"
              >
                {{ submitting ? 'AI 正在全力分析中...' : '提交 AI 审核' }}
              </el-button>
            </el-form>
          </el-card>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="15" :lg="16">
        <el-card v-if="auditResult" shadow="never" class="result-card">
          <div slot="header" class="card-header-flex">
            <div class="header-left">
              <span class="res-title"><i class="el-icon-data-analysis"></i> 识别报告</span>
              <span class="task-id-text">ID: {{ auditResult.task_id }}</span>
            </div>
            <el-tag v-if="auditResult.model_name" class="custom-model-tag">
              <i class="el-icon-cpu"></i> {{ auditResult.model_name }}
            </el-tag>
          </div>

          <el-row :gutter="30">
            <el-col :span="24" :lg="10">
              <div class="result-preview-container">
                <el-image
                  :src="auditResult.image_url"
                  fit="contain"
                  :preview-src-list="[auditResult.image_url]"
                  class="final-image"
                >
                  <div slot="placeholder" class="image-loading">加载中...</div>
                </el-image>
                <p class="img-caption">送审图像预览</p>
              </div>
            </el-col>

            <el-col :span="24" :lg="14">
              <el-table :data="auditResult.results" border stripe size="small" class="res-table">
                <el-table-column label="检测项 (Prompt)" prop="prompt" show-overflow-tooltip width="150"></el-table-column>
                <el-table-column label="AI 分析结果">
                  <template slot-scope="scope">
                    <div class="result-content-box">
                      <div class="raw-result">
                        <span class="lang-label">EN</span> {{ scope.row.result }}
                      </div>

                      <div v-if="scope.row.result_zh || scope.row.chs_translation" class="zh-result-box">
                        <el-divider><i class="el-icon-chat-dot-square"></i> 中文解析</el-divider>
                        <p class="zh-text">{{ scope.row.result_zh || scope.row.chs_translation }}</p>
                      </div>

                      <el-button
                        v-else
                        type="text"
                        size="mini"
                        icon="el-icon-refresh"
                        @click="handleTranslate(scope.row)"
                      >翻译为中文</el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </el-col>
          </el-row>
        </el-card>

        <el-empty v-else :image-size="200" description="暂无数据，请在左侧上传并提交审核任务"></el-empty>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import axios from 'axios';
import { translate } from "@/api/ai";

export default {
  name: 'ImageAudit',
  data() {
    return {
      submitting: false,
      file: null,
      previewUrl: '',
      prompts: ['Analyze the overall sentiment of the image.', 'Check for any restricted objects.'],
      auditResult: null
    };
  },
  methods: {
    handleFileChange(file) {
      this.file = file.raw;
      this.previewUrl = URL.createObjectURL(file.raw);
    },
    addPrompt() {
      this.prompts.push('');
    },
    removePrompt(index) {
      this.prompts.splice(index, 1);
    },
    async submitAudit() {
      if (!this.file) return this.$message.warning('请先选择待审核图片');

      this.submitting = true;
      try {
        const formData = new FormData();
        formData.append('file', this.file);
        this.prompts.filter(p => p.trim()).forEach(p => formData.append('prompts', p));

        const res = await axios.post('/api1/image/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        this.auditResult = res.data;
        this.$message.success('审核任务处理成功');
      } catch (error) {
        const msg = error.response?.data?.detail || '服务繁忙，请稍后再试';
        this.$message.error(`审核失败: ${msg}`);
      } finally {
        this.submitting = false;
      }
    },
    async handleTranslate(row) {
      this.$set(row, 'result_zh', '正在翻译...');
      try {
        const resp = await translate({ text: row.result, target: 'zh' });
        this.$set(row, 'result_zh', resp.translation);
      } catch (error) {
        this.$message.error('翻译服务连接失败');
        this.$set(row, 'result_zh', '');
      }
    }
  }
};
</script>

<style scoped>
/* 容器去掉背景，复用 AuditIndex 的背景 */
.image-audit-sub-container {
  padding: 0;
}

/* 页面顶部标题样式 */
.page-top-bar {
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e6e9f0;
}
.main-title {
  margin: 0;
  font-size: 22px;
  color: #2c3e50;
  font-weight: 600;
}
.sub-title {
  font-size: 13px;
  color: #7f8c8d;
}

/* 布局辅助 */
.sticky-form {
  position: sticky;
  top: 80px; /* 避开 AuditIndex 的导航栏 */
}

/* 卡片美化 */
.config-card, .result-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.card-header {
  font-weight: bold;
  color: #34495e;
}

/* 上传控件自定义 */
.image-uploader {
  width: 100%;
}
::v-deep .el-upload-dragger {
  width: 100%;
  height: 180px;
}
.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
}
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f8f9fb;
}
.upload-mask {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4);
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: 0.3s;
}
.upload-preview:hover .upload-mask {
  opacity: 1;
}

/* 动态 Prompt 行 */
.prompt-input-row {
  margin-bottom: 10px;
}
.add-prompt-btn {
  padding: 5px 0;
  font-size: 13px;
}

.submit-btn {
  width: 100%;
  height: 40px;
  font-weight: bold;
}

/* 结果展示区 */
.card-header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.task-id-text {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 10px;
}
.res-title {
  font-weight: bold;
  color: #334155;
}

.result-preview-container {
  text-align: center;
  background: #f1f5f9;
  padding: 15px;
  border-radius: 8px;
}
.final-image {
  max-width: 100%;
  height: 280px;
  border-radius: 4px;
}
.img-caption {
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
}

/* 表格内识别结果美化 */
.result-content-box {
  padding: 5px 0;
}
.lang-label {
  display: inline-block;
  font-size: 10px;
  background: #e2e8f0;
  color: #475569;
  padding: 0 4px;
  border-radius: 3px;
  margin-right: 5px;
}
.raw-result {
  color: #1e293b;
  line-height: 1.6;
}
.zh-result-box {
  margin-top: 10px;
  background: #f8fafc;
  padding: 10px;
  border-radius: 6px;
}
.zh-text {
  color: #059669; /* 绿色系表示已解析结果 */
  font-weight: 500;
  margin: 0;
}

::v-deep .el-divider--horizontal {
  margin: 15px 0 10px 0;
}
::v-deep .el-divider__text {
  font-size: 11px;
  color: #94a3b8;
  background: #f8fafc;
}

/* 模型 Tag 保持科技感 */
.custom-model-tag {
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: white !important;
  border: none;
  border-radius: 20px;
  padding: 0 15px;
}
</style>
